import { Fragment, useState } from 'react';
import { Menu, Transition } from '@headlessui/react';
import { ChevronDownIcon, CheckIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { cn } from '@lib/utils/cn';
import { Badge } from './Badge';

export interface MultiSelectOption {
  id: string;
  label: string;
  description?: string;
}

interface MultiSelectProps {
  label: string;
  options: MultiSelectOption[];
  selected: string[];
  onChange: (selected: string[]) => void;
  placeholder?: string;
  error?: string;
  disabled?: boolean;
  loading?: boolean;
  className?: string;
  'data-testid'?: string;
}

export function MultiSelect({
  label,
  options,
  selected,
  onChange,
  placeholder = 'Seleccionar...',
  error,
  disabled = false,
  loading = false,
  className,
  'data-testid': testId,
}: MultiSelectProps) {
  const [searchText, setSearchText] = useState('');

  const subTestId = (suffix: string) => (testId ? `${testId}-${suffix}` : undefined);

  // `option.label` está tipado como `string` no-nulable, pero quien arma
  // `options` a veces lo llena con un dato que en la práctica SÍ puede venir
  // `null` desde Aludra (ej. `company.name` — ver CompanyCard.tsx, mismo
  // problema). Antes esto tumbaba la pantalla completa (EditUserModal) con
  // `TypeError: Cannot read properties of null (reading 'toLowerCase')`.
  const filteredOptions = options.filter((option) =>
    (option.label ?? '').toLowerCase().includes(searchText.toLowerCase())
  );

  const toggleOption = (optionId: string) => {
    if (selected.includes(optionId)) {
      onChange(selected.filter((id) => id !== optionId));
    } else {
      onChange([...selected, optionId]);
    }
  };

  const removeOption = (optionId: string) => {
    onChange(selected.filter((id) => id !== optionId));
  };

  const clearAll = () => {
    onChange([]);
  };

  // Devuelve {id, label} — no solo el label — para poder buscar el `id` de
  // cada chip por su lugar en esta lista en vez de reugscarlo por
  // `option.label === label` más abajo: con un label vacío/`null` (mismo
  // problema de arriba) esa búsqueda por texto no distingue dos opciones
  // distintas que compartan el mismo label y siempre encuentra la primera.
  const getSelectedOptions = () => {
    return options.filter((opt) => selected.includes(opt.id));
  };

  return (
    <div data-testid={testId} className={cn('space-y-2', className)}>
      <label className="text-sm font-medium text-secondary-900 dark:text-white">
        {label}
      </label>

      <Menu as="div" className="relative">
        {({ open }) => (
          <>
            <Menu.Button
              disabled={disabled || loading}
              data-testid={subTestId('trigger')}
              className={cn(
                'relative w-full rounded-lg border py-2 px-3 text-left',
                'bg-white dark:bg-secondary-800',
                'border-secondary-300 dark:border-secondary-600',
                'focus:border-primary-500 focus:ring-1 focus:ring-primary-500',
                'disabled:opacity-50 disabled:cursor-not-allowed',
                'transition-colors',
                error && 'border-danger-500 focus:border-danger-500 focus:ring-danger-500'
              )}
            >
              <div className="flex items-center justify-between gap-2">
                <div className="flex-1 min-w-0">
                  {selected.length === 0 ? (
                    <span className="text-secondary-400 dark:text-secondary-500">
                      {loading ? 'Cargando...' : placeholder}
                    </span>
                  ) : (
                    <div className="flex flex-wrap gap-1">
                      {getSelectedOptions().map((option) => (
                        <Badge
                          key={option.id}
                          variant="primary"
                          size="sm"
                          className="flex items-center gap-1"
                        >
                          <span className="max-w-[150px] truncate">{option.label || 'Sin nombre'}</span>
                          <span
                            role="button"
                            tabIndex={0}
                            data-testid={subTestId(`remove-${option.id}`)}
                            onClick={(e) => {
                              e.stopPropagation();
                              removeOption(option.id);
                            }}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' || e.key === ' ') {
                                e.stopPropagation();
                                removeOption(option.id);
                              }
                            }}
                            className="hover:bg-primary-700 rounded-full p-0.5 cursor-pointer"
                          >
                            <XMarkIcon className="w-3 h-3" />
                          </span>
                        </Badge>
                      ))}
                    </div>
                  )}
                </div>
                <ChevronDownIcon
                  className={cn(
                    'w-5 h-5 text-secondary-400 transition-transform',
                    open && 'transform rotate-180'
                  )}
                />
              </div>
            </Menu.Button>

            <Transition
              as={Fragment}
              enter="transition ease-out duration-100"
              enterFrom="transform opacity-0 scale-95"
              enterTo="transform opacity-100 scale-100"
              leave="transition ease-in duration-75"
              leaveFrom="transform opacity-100 scale-100"
              leaveTo="transform opacity-0 scale-95"
            >
              <Menu.Items
                className={cn(
                  'absolute z-50 mt-2 w-full origin-top rounded-md',
                  'bg-white dark:bg-secondary-800 shadow-lg ring-1 ring-black ring-opacity-5',
                  'focus:outline-none max-h-60 overflow-auto'
                )}
              >
                <div className="p-2 space-y-1">
                  {/* Search input */}
                  <input
                    type="text"
                    placeholder="Buscar..."
                    value={searchText}
                    onChange={(e) => setSearchText(e.target.value)}
                    data-testid={subTestId('search-input')}
                    className={cn(
                      'w-full px-3 py-2 text-sm rounded-md',
                      'bg-secondary-50 dark:bg-secondary-700',
                      'border border-secondary-200 dark:border-secondary-600',
                      'focus:outline-none focus:border-primary-500 focus:ring-1 focus:ring-primary-500',
                      'text-secondary-900 dark:text-white'
                    )}
                    onClick={(e) => e.stopPropagation()}
                  />

                  {/* Clear all button */}
                  {selected.length > 0 && (
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        clearAll();
                      }}
                      data-testid={subTestId('clear-button')}
                      className={cn(
                        'w-full px-3 py-2 text-sm text-left rounded-md',
                        'text-danger-600 dark:text-danger-400',
                        'hover:bg-danger-50 dark:hover:bg-danger-900/20',
                        'transition-colors'
                      )}
                    >
                      Limpiar selección
                    </button>
                  )}

                  {/* Options list */}
                  <div className="border-t border-secondary-200 dark:border-secondary-700 pt-1">
                    {filteredOptions.length === 0 ? (
                      <div className="px-3 py-2 text-sm text-secondary-500 dark:text-secondary-400">
                        No se encontraron resultados
                      </div>
                    ) : (
                      filteredOptions.map((option, index) => {
                        const isSelected = selected.includes(option.id);
                        return (
                          <Menu.Item key={`${option.id}-${index}`}>
                            {({ active }) => (
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  toggleOption(option.id);
                                }}
                                data-testid={subTestId(`option-${option.id}`)}
                                className={cn(
                                  'w-full px-3 py-2 text-sm text-left rounded-md',
                                  'flex items-start gap-3 transition-colors',
                                  active &&
                                    'bg-secondary-100 dark:bg-secondary-700'
                                )}
                              >
                                <div
                                  className={cn(
                                    'flex-shrink-0 w-5 h-5 mt-0.5 rounded border-2 flex items-center justify-center',
                                    isSelected
                                      ? 'bg-primary-600 border-primary-600'
                                      : 'border-secondary-300 dark:border-secondary-600'
                                  )}
                                >
                                  {isSelected && (
                                    <CheckIcon className="w-3 h-3 text-white" />
                                  )}
                                </div>
                                <div className="flex-1 min-w-0">
                                  <div className="font-medium text-secondary-900 dark:text-white">
                                    {option.label || 'Sin nombre'}
                                  </div>
                                  {option.description && (
                                    <div className="text-xs text-secondary-500 dark:text-secondary-400 mt-0.5">
                                      {option.description}
                                    </div>
                                  )}
                                </div>
                              </button>
                            )}
                          </Menu.Item>
                        );
                      })
                    )}
                  </div>
                </div>
              </Menu.Items>
            </Transition>
          </>
        )}
      </Menu>

      {error && (
        <p className="text-sm text-danger-600 dark:text-danger-400">{error}</p>
      )}
    </div>
  );
}
